import React from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Modal from 'modules/views/shared/Modal';
import { ReactComponent as ExclamationRound } from 'assets/images/ExclamationRound.svg';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { deleteMyCertification } from 'modules/state-mgmt/workerData';

import { actionMapForOtherLoaders } from 'constants/actions';
import { useStylesDeleteCertificationModal } from 'modules/views/pages/Certifications/styles';
import { Certificate } from 'modules/views/pages/Certifications/types';

interface IDeleteMyCertificationModalProps {
  certificate: Certificate;
  onClose: () => void;
  onDeleteSuccess: () => void;
  isVisible: boolean;
  // Refresh params
  pageNumber: number;
  pageSize: number;
  filterCertificateStatus: number;
  sortCriteria?: string;
  projectIds?: string[];
}

const DeleteMyCertificationModal = ({
  certificate,
  onClose,
  onDeleteSuccess,
  isVisible,
  pageNumber,
  pageSize,
  filterCertificateStatus,
  sortCriteria,
  projectIds,
}: IDeleteMyCertificationModalProps) => {
  const { t } = useTranslation();
  const classes = useStylesDeleteCertificationModal();
  const dispatch = useAppDispatch();
  const { workerId, isDeleteCertificationLoading, languageCode } = useAppSelector(state => ({
    workerId: state.onboarding.id,
    isDeleteCertificationLoading: state.loader.otherLoaders[actionMapForOtherLoaders.deleteMyCertification]?.isLoading,
    languageCode: state.app?.languageCode,
  }));

  const onDeleteCertification = () => {
    dispatch(
      deleteMyCertification({
        workerId,
        certificateId: certificate.id,
        languageCode,
        pageNumber,
        pageSize,
        filterCertificateStatus,
        sortCriteria,
        projectIds,
      }),
    )
      .then(res => {
        if (res.type.includes('fulfilled')) {
          onDeleteSuccess();
          onClose();
        } else {
          enqueueSnackbar(t('certifications.deleteCertificateModal.errors.failedToDelete'), { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar(t('certifications.deleteCertificateModal.errors.failedToDelete'), { variant: 'error' });
      });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      modalTitleStyleClass={classes.modalTitle}
      modalContentStyleClass={classes.modalContent}
      modalActionsStyleClass={classes.modalActions}
      testId="delete-my-certification-modal"
      Content={
        <>
          <div className={classes.imageParent}>
            <ExclamationRound />
          </div>
          <p className={classes.title}>{t('certifications.deleteCertificateModal.title')}</p>
          <p className={classes.warningText}>
            {t('certifications.deleteCertificateModal.aboutToDelete')}
            <span className={classes.certificationName}> "{certificate.certification.name}" </span>
            {t('certifications.deleteCertificateModal.certification')}{' '}
            {t('certifications.deleteCertificateModal.mayAffectBadge')}
          </p>
        </>
      }
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.button,
          testId: 'delete-my-certification-cancel-button',
          onClick: onClose,
        },
        {
          label: t('common.delete'),
          styleClass: classes.button,
          testId: 'delete-my-certification-confirm-button',
          isLoading: isDeleteCertificationLoading,
          onClick: onDeleteCertification,
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(DeleteMyCertificationModal);
